import type { CaseStudy } from '../../types/content'

export const pranaPhysio: CaseStudy = {
  slug: 'prana-physio',
  summary:
    'A self-initiated concept. We invented a two-clinic physiotherapy practice with nine practitioners and a reception team buried in booking calls, then designed a booking experience short enough to finish in an auto.',
  overview: {
    industry: 'Concept · Private healthcare',
    location: 'Concept · Ahmedabad, Gujarat',
    timeline: 'Self-initiated concept, 2026',
    services: ['Product strategy', 'Booking flow design', 'Website design', 'Prototype'],
  },
  problem: {
    heading: 'The problem',
    paragraphs: [
      'This is concept work. No clinic commissioned it and Prana Physiotherapy does not exist. We built it because booking is the most commonly broken flow in local healthcare, and because a concept lets us design the whole flow rather than the part a client has budget for.',
      'The premise: two clinics, nine practitioners, and a booking form that technically works. In practice almost every appointment still gets made by phone or WhatsApp, and the reception team spends the morning reading availability aloud.',
      'The form is the reason. It asks for fourteen fields before showing a single available time, including cashless mediclaim details, a TPA number most patients do not have to hand, and a free-text description of the injury. It offers a dropdown of nine practitioners with no explanation of what any of them specialise in, and it sits in a third-party widget that breaks below 380 pixels — which is where most of the traffic is. The cost is not only staff time: calls only get answered between nine and seven, so every evening enquiry becomes an unread message, and some share of those becomes an appointment at a clinic down the road.',
    ],
  },
  research: {
    heading: 'Assumptions and discovery',
    paragraphs: [
      'There is no clinic data behind this. What follows are the assumptions the design is built on, drawn from how appointment booking tends to behave rather than from a study we did not run. They are stated so the design can be judged against them.',
      'The central assumption is that most callers have exactly one question — when can I be seen — and that the job is not to persuade people to book online but to answer that question faster than a phone call can. We modelled a reception call log of 41 calls categorised by intent to hold the assumption still while we designed against it.',
      'The second assumption is that patients choose by problem, not by name. Running injuries, post-surgical rehab, back and neck, pregnancy — then earliest availability. A nine-name dropdown asks a question a first-time patient has no way to answer.',
      'The third is that the paperwork is in the wrong place: nobody has a TPA number on them in an auto, and reception validates it manually either way. And we assumed a real diary with constraints that cannot move — appointment durations, room availability, the gap after an initial assessment, two practitioners sharing a day — because a flow that only works against an idealised calendar is not a flow. Each of these would be validated in week one of a real engagement; here they are the brief.',
    ],
  },
  artifacts: [
    {
      kind: 'audit',
      title: 'Reception call log',
      caption: 'Modelled: 41 calls categorised by intent and handling time',
    },
    {
      kind: 'flow',
      title: 'Three-tap booking flow',
      caption: 'Problem, then time, then details — availability shown first',
    },
    {
      kind: 'sitemap',
      title: 'Site structure',
      caption: 'Nine pages, with booking reachable from every one of them',
    },
  ],
  strategy: {
    heading: 'Strategy and decisions',
    paragraphs: [
      'The strategy was to invert the form. Show availability before asking for anything, and collect only what is legally and clinically necessary to hold the slot. Everything else moves to a pre-appointment message the patient can complete at home, where the mediclaim card actually is.',
      'We reduced the flow to three decisions: what is the problem, when suits you, who are you. Practitioner selection was removed as a step and replaced with automatic matching by specialism, with an optional override for returning patients who want the same person.',
      'The site itself was scoped down to support that goal. Nine pages, a persistent booking action on every one, and no content that exists to fill a navigation slot. We cut a blog a practice like this would never write and a testimonials page in favour of two short patient notes on the pages where hesitation actually happens.',
    ],
  },
  decisions: [
    {
      decision: 'Showed availability before asking for any details',
      reasoning:
        'If the only question most callers have is "when", asking fourteen questions before answering it is the entire failure.',
    },
    {
      decision: 'Removed practitioner choice as a required step',
      reasoning:
        'Patients choose by problem, not by name. Automatic matching by specialism with an optional override respects both first-timers and regulars.',
    },
    {
      decision: 'Moved mediclaim and referral details to a pre-visit message',
      reasoning:
        'Nobody has their TPA number on them in an auto. Collecting it at the point of booking costs bookings and gains nothing, because reception validates it manually anyway.',
    },
    {
      decision: 'Sent the confirmation on WhatsApp as well as email',
      reasoning:
        'The confirmation is the moment the patient decides whether the practice is organised, and it needs to land where they will actually see it. Designing that screen first set the standard for everything upstream.',
    },
  ],
  design: {
    heading: 'Design direction',
    paragraphs: [
      'Clinical without being cold. The palette is a soft chalk ground, one deep ink for type and a single muted teal reserved exclusively for the booking action, so the primary path is never ambiguous. No stock photography of smiling strangers: the imagery is the clinic itself, shot in morning light, which also answers the quiet question of whether the place is clean.',
      'Everything is built mobile-first at a genuinely small breakpoint, because the traffic is people on phones in pain, often on a patchy connection. Tap targets are 48 pixels minimum, the time grid is a two-column list rather than a calendar widget, and the whole flow is legible at 200% zoom. Contrast passes AA throughout, which matters more than usual for a practice whose patients include people recovering from concussion.',
      'The component set is deliberately tiny: a problem selector, a time grid, a three-field detail form, a confirmation card, and a practitioner card used on the team page. Five components, nine pages, and a diary a reception team can still override by hand.',
    ],
  },
  typefaces: [
    { name: 'Inter Tight', role: 'Interface, booking flow and body copy' },
    { name: 'Instrument Serif', role: 'Page headlines only' },
  ],
  palette: [
    { name: 'Chalk', hex: '#F7F8F7' },
    { name: 'Ink', hex: '#111513' },
    { name: 'Teal action', hex: '#1F6F63' },
    { name: 'Hairline', hex: '#E2E6E3' },
  ],
  components: [
    'Problem selector',
    'Two-column time grid',
    'Three-field detail form',
    'Confirmation card',
    'Practitioner card',
  ],
  mockupCaption: 'Booking flow and clinic pages, mobile-first with desktop layout',
  before: {
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/4bfda3f4-b593-45ee-ad50-eb67f2011dee.jpg',
    alt: 'The starting point in the concept: a teal and green layout with a banner slideshow and a cluttered sidebar',
  },
  results: {
    isProjected: true,
    intro:
      'Nothing here has been measured in market. These are the outcomes the design was built to hit, stated as targets so they can be argued with.',
    metrics: [
      { value: 'Under 1 min', label: 'Target time to complete a booking' },
      { value: '3 taps', label: 'Target: problem, time, details — nothing else' },
      { value: 'After hours', label: 'Target: bookings taken when the clinic is closed' },
      { value: '5 states', label: 'Booking flow states designed, including failure' },
    ],
    note:
      'We would judge this successful if bookings kept happening after the phones stopped being answered — appointments that would otherwise become unread messages. That is the goal the flow was designed for, not a measured result.',
  },
}
